// The multi-select filter panel shared by Payment Collections and Current
// Payment Collections.
//
// Reuses ColumnFilter (the searchable multi-select built for the Ledger
// Workspace) and DayRangeCalendar, so a filter behaves the same wherever it
// appears rather than each page growing its own controls.
//
// The column definitions, the option lists and the matcher all live here
// together: a filter that offered a value the matcher couldn't match would be
// a silent dead end, and keeping them side by side is what stops that.

import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import ColumnFilter, { BLANK_VALUE } from './ColumnFilter';
import DayRangeCalendar from './DayRangeCalendar';
import { billTypeLabel } from '../constants/billingTypes';
import { dutyRank } from '../constants/shifts';
import { clientLabel, effectiveStatus } from '../utils/billStatus';
import { downloadCollectionsExcel, downloadCollectionsPdf } from '../utils/collectionsExport';

export interface FilterableBill {
  total_amount: number;
  amount_received: number;
  status: string;
  bill_type: string | null;
  duty_type?: string | null;
  case_manager?: string | null;
  client_name?: string | null;
  patient_name: string;
  expected_payment_date?: string | null;
}

/** colId -> the values ticked for it. Absent or empty means "no filter". */
export type ColumnFilters = Record<string, string[]>;

interface FilterColumn {
  id: string;
  label: string;
  /** '' means the row has no value here, and is offered as "(blanks)". */
  get: (b: FilterableBill) => string;
  /** How to order the option list. Defaults to A→Z. */
  compareOptions?: (a: string, b: string) => number;
}

// Outstanding first — it's what the page is for. Alphabetically these would
// read Collected, Pending, Processed, burying the actionable one in the middle.
const STATUS_ORDER = ['Pending', 'Collected', 'Processed'];
const rankIn = (order: string[]) => (a: string, b: string) => {
  const ia = order.indexOf(a), ib = order.indexOf(b);
  return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib) || a.localeCompare(b);
};

export const COLLECTION_FILTER_COLUMNS: FilterColumn[] = [
  // Derived, not the stored status — a part-paid bill filters as Pending here
  // exactly as it displays in the table. Multi-select, so Processed can be
  // picked out on its own rather than folded in with Collected.
  { id: 'status',      label: 'Status',       get: b => effectiveStatus(b), compareOptions: rankIn(STATUS_ORDER) },
  { id: 'dutyType',    label: 'Duty Type',    get: b => b.duty_type || '', compareOptions: (a, c) => dutyRank(a) - dutyRank(c) || a.localeCompare(c) },
  { id: 'billType',    label: 'Type',         get: b => billTypeLabel(b.bill_type) },
  // Both trimmed for the same reason clientLabel is: a stored name with a stray
  // leading space would otherwise appear as its own option, separate from and
  // indistinguishable from the clean one, and neither would match every row.
  { id: 'caseManager', label: 'Case Manager', get: b => (b.case_manager || '').trim() },
  { id: 'client',      label: 'Client',       get: b => clientLabel(b) },
];

const dayOf = (d?: string | null) => (d ? String(d).split('T')[0] : '');

/**
 * Options come from the whole month, not the already-filtered rows, so
 * narrowing one dropdown never makes another dropdown's choices vanish.
 */
export function collectionFilterOptions(bills: FilterableBill[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const col of COLLECTION_FILTER_COLUMNS) {
    const seen = new Set<string>();
    let hasBlank = false;
    for (const b of bills) {
      const v = col.get(b);
      if (!v) hasBlank = true;
      else seen.add(v);
    }
    const values = Array.from(seen).sort(col.compareOptions || ((a, b) => a.localeCompare(b)));
    // "(blanks)" last, so "which bills have no case manager?" is askable.
    out[col.id] = hasBlank ? [...values, BLANK_VALUE] : values;
  }
  return out;
}

/** True when the bill survives every active filter. */
export function matchesCollectionFilters(
  bill: FilterableBill,
  filters: ColumnFilters,
  dateFrom: string,
  dateTo: string,
): boolean {
  for (const col of COLLECTION_FILTER_COLUMNS) {
    const picked = filters[col.id];
    if (!picked || picked.length === 0) continue;   // untouched column
    if (!picked.includes(col.get(bill) || BLANK_VALUE)) return false;
  }
  if (dateFrom || dateTo) {
    const d = dayOf(bill.expected_payment_date);
    // No expected date can't satisfy a date range — it isn't due in it.
    if (!d) return false;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
  }
  return true;
}

/** Counts what's actually narrowing anything, for the badge on the button. */
export function activeFilterCount(filters: ColumnFilters, dateFrom: string, dateTo: string): number {
  const cols = COLLECTION_FILTER_COLUMNS.filter(c => (filters[c.id] || []).length > 0).length;
  return cols + (dateFrom || dateTo ? 1 : 0);
}

interface Props {
  /** The month's rows before filtering — the source of the option lists. */
  bills: FilterableBill[];
  /** The rows left after filtering — what the download contains. */
  filteredBills: any[];
  filters: ColumnFilters;
  onFiltersChange: (next: ColumnFilters) => void;
  dateFrom: string;
  dateTo: string;
  onDateChange: (from: string, to: string) => void;
  /** For the calendar, which shows exactly the month being viewed. */
  year: number;
  /** 1-indexed. */
  month: number;
  /** Names the page in the download's heading and file name. */
  title: string;
  /** Reference numbers by patient id; omitted on the page with no SL column. */
  /** Case → SL, for the export's SL column. Supplied only where there is one. */
  slNos?: Record<number, number | null>;
}

const CollectionFilters: React.FC<Props> = ({
  bills, filteredBills, filters, onFiltersChange, dateFrom, dateTo, onDateChange,
  year, month, title, slNos,
}) => {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
  const options = useMemo(() => collectionFilterOptions(bills), [bills]);
  const count = activeFilterCount(filters, dateFrom, dateTo);

  const clearAll = () => {
    onFiltersChange({});
    onDateChange('', '');
  };

  const periodLabel = new Date(year, month - 1)
    .toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const prettyDay = (d: string) =>
    d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB',
      { day: '2-digit', month: 'short', year: 'numeric' }) : '';

  const dateLabel =
    !dateFrom && !dateTo ? 'All dates'
    : dateFrom && dateTo && dateFrom === dateTo ? prettyDay(dateFrom)
    : dateFrom && dateTo ? `${prettyDay(dateFrom)} – ${prettyDay(dateTo)}`
    : dateFrom ? `From ${prettyDay(dateFrom)}`
    : `Up to ${prettyDay(dateTo)}`;

  // Every filter gets a line whether or not it's narrowing anything — a reader
  // holding the print-out can't otherwise tell a filter set to All from one
  // that was never offered.
  const exportSummary = (): [string, string][] => [
    ...COLLECTION_FILTER_COLUMNS.map(col => {
      const picked = filters[col.id] || [];
      return [
        col.label,
        picked.length === 0
          ? 'All'
          : picked.map(v => (v === BLANK_VALUE ? '(blanks)' : v)).join(' + '),
      ] as [string, string];
    }),
    ['Expected Payment', dateLabel],
  ];

  const fileBase = `${title.replace(/\s+/g, '-')}_${
    (!dateFrom && !dateTo) ? periodLabel
    : dateFrom && dateTo && dateFrom === dateTo ? prettyDay(dateFrom)
    : dateFrom && dateTo ? `${prettyDay(dateFrom)} to ${prettyDay(dateTo)}`
    : dateFrom ? `from ${prettyDay(dateFrom)}`
    : `upto ${prettyDay(dateTo)}`}`;

  const runExport = async (kind: 'pdf' | 'excel') => {
    if (filteredBills.length === 0) return;
    setExporting(kind);
    try {
      const opts = { rows: filteredBills, slNos, title, periodLabel, summary: exportSummary(), fileBase };
      if (kind === 'excel') await downloadCollectionsExcel(opts);
      else downloadCollectionsPdf(opts);
      toast.success(`Downloaded ${filteredBills.length} bill${filteredBills.length === 1 ? '' : 's'}`);
    } catch (err) {
      console.error('collections export failed', err);
      toast.error('Could not build the download');
    } finally {
      setExporting(null);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '6px 16px', borderRadius: '8px', cursor: 'pointer',
            fontSize: '0.84rem', fontWeight: 600, border: '1px solid',
            borderColor: count > 0 ? '#2563eb' : '#e2e8f0',
            backgroundColor: count > 0 ? '#eff6ff' : 'white',
            color: count > 0 ? '#1d4ed8' : '#64748b',
          }}
          title="Filter by status, duty type, bill type, case manager, client or expected payment date"
        >
          <span style={{ display: 'inline-block', width: 8, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .12s' }}>⌄</span>
          Filters
          {count > 0 && (
            <span style={{
              background: '#2563eb', color: '#fff', borderRadius: '9999px',
              fontSize: '0.7rem', fontWeight: 700, padding: '0 6px', lineHeight: '16px',
            }}>{count}</span>
          )}
        </button>

        {/* Beside the toggle rather than inside the panel, so filters can be
            cleared without opening it — and so it's visible at all while the
            panel is shut but something is still narrowing the table. */}
        {count > 0 && (
          <button
            onClick={clearAll}
            style={{
              padding: '6px 14px', borderRadius: '8px', border: '1px solid #e2e8f0',
              background: 'white', color: '#dc2626', fontSize: '0.84rem', fontWeight: 600,
              cursor: 'pointer',
            }}
            title="Clear every filter"
          >
            Clear ✕
          </button>
        )}
      </div>

      {open && (
        <div style={{
          width: '100%', backgroundColor: 'white', border: '1px solid #e2e8f0',
          borderRadius: '12px', padding: '1rem 1.25rem', marginTop: '0.75rem',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.9rem',
        }}>
          {COLLECTION_FILTER_COLUMNS.map(col => (
            <div key={col.id} style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
              <span style={labelStyle}>{col.label}</span>
              <ColumnFilter
                label={col.label}
                options={options[col.id] || []}
                selected={filters[col.id] || []}
                onChange={next => onFiltersChange({ ...filters, [col.id]: next })}
              />
            </div>
          ))}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            <span style={labelStyle}>Expected Payment</span>
            <DayRangeCalendar
              year={year}
              month={month}
              from={dateFrom}
              to={dateTo}
              onChange={onDateChange}
            />
          </div>

          {/* "Sort by SL" was offered here while the numbers were typed by hand
              and could disagree with the reading order. SL is now derived from
              that order, so the tick could only ever be a no-op. */}

          {/* Downloads exactly the rows left on screen — filters, search and
              all. Sits in the panel because it is downstream of the filters. */}
          {/* Two distinct buttons rather than one split control — each is its
              own action, and the colours match what people expect of the two
              file types. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            <span style={labelStyle}>Download filtered</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => runExport('pdf')}
                disabled={exporting !== null || filteredBills.length === 0}
                style={downloadBtn('#dc2626', '#fef2f2', '#fecaca', filteredBills.length === 0)}
                title="Download what's on screen as a PDF"
              >
                <span aria-hidden style={{ fontSize: '0.95rem', lineHeight: 1 }}>⭳</span>
                {exporting === 'pdf' ? 'Working…' : 'PDF'}
              </button>
              <button
                onClick={() => runExport('excel')}
                disabled={exporting !== null || filteredBills.length === 0}
                style={downloadBtn('#15803d', '#f0fdf4', '#bbf7d0', filteredBills.length === 0)}
                title="Download what's on screen as an Excel sheet"
              >
                <span aria-hidden style={{ fontSize: '0.95rem', lineHeight: 1 }}>⭳</span>
                {exporting === 'excel' ? 'Working…' : 'Excel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Each file type gets its own coloured button, sized to the same height as a
// filter dropdown so the panel's grid stays even.
const downloadBtn = (
  color: string, bg: string, border: string, off: boolean,
): React.CSSProperties => ({
  flex: 1, minWidth: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  padding: '0.45rem 0.3rem', borderRadius: '6px',
  border: `1px solid ${off ? '#e2e8f0' : border}`,
  background: off ? '#f8fafc' : bg,
  color: off ? '#94a3b8' : color,
  fontSize: '0.825rem', fontWeight: 700, fontFamily: 'inherit',
  cursor: off ? 'default' : 'pointer',
});

const labelStyle: React.CSSProperties = {
  fontSize: '0.68rem', fontWeight: 600, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.04em',
};

export default CollectionFilters;
